import { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { usePcfValidationStore } from '../../../shared/stores/usePcfValidationStore';
import {
  collectPipingSystemsGrpc,
  runExportFlow,
  type ExportFlowStep,
  type RunExportFlowOptions,
} from '../../../shared/api/grpc/pcfExportClient';
import type { RevitOperationProgressSnapshot } from '../../../shared/api/grpc/operationProgressClient';
import { getGrpcPort, setGrpcPort } from '../../../shared/api/grpc/transport';
import { useConfigurationStore } from '../../../shared/stores/useConfigurationStore';
import { useExportQuotaStore } from '../../../shared/stores/useExportQuotaStore';
import { useBatchOperation } from '../../../shared/lib/batchOperations';
import { toGroupBy, toSystemGroupingPreference } from '../lib/systemGroupingBridge';
import { filterSystemsByGroupValues, groupSystemsByParameterValue } from '../lib/parameterGrouping';
import type { PipingSystem, ExportResult, ExportConfig, GroupBy } from '../types';
import {
  mapRevitOperationSnapshotToProgressModel,
  type OperationProgressModel,
} from '../../../shared/ui/progress';
import { registerRevitModelCacheInvalidator } from '../../../shared/lib/revit/modelCacheInvalidation';
import { useActiveDocumentRefresh } from '../../../shared/hooks/ActiveDocumentProvider';
import { markPipingSystemValidationPending } from '../lib/exportSystemStatus';
import { mapLocalPcfProgressToModel } from '../lib/exportProgressModel';
import { isE2eMockSystemsRequested } from '../../../shared/api/grpc/pcfExportClient';
import {
  addIssueElementIds,
  collectIssueSystemNames,
  createExportTraceId,
  emitExportTrace,
  getExportErrorMessage,
  getExportTraceElapsedMs,
  getExportTraceNowMs,
  getSessionExportCountKey,
  getSystemField,
  isActionableValidationIssue,
  isErrorSeverity,
  isGlobalSystemScopeName,
  isUnknownComMemberError,
  isWarningSeverity,
  logExportDebug,
  normalizeSystemNameToken,
  normalizeTransportError,
  parseSystemNames,
  scheduleAfterInitialPaint,
} from '../lib/exportStateUtils';

const EXPORT_OUTPUT_PATH_STORAGE_KEY = 'pipeex-export-output-path-v1';

const readPersistedOutputPath = (): string => {
  try {
    if (typeof window === 'undefined') {
      return '';
    }
    return window.localStorage.getItem(EXPORT_OUTPUT_PATH_STORAGE_KEY) ?? '';
  } catch {
    return '';
  }
};

type GrpcPortDiscovery = {
  ports: number[];
  hostBridgeAvailable: boolean;
  error: string | null;
};

export interface UseExportStateReturn {
  systems: PipingSystem[];
  /** Table rows: in Parameter grouping mode the systems folded into one row
   *  per parameter value, otherwise identical to `systems`. Export logic keeps
   *  reading `systems`; only presentation should consume `viewSystems`. */
  viewSystems: PipingSystem[];
  loading: boolean;
  systemsStale: boolean;
  error: string | null;
  exporting: boolean;
  exportProgress: OperationProgressModel | null;
  exportResult: ExportResult | null;
  exportStep: ExportFlowStep | null;
  exportTraceId: string | null;
  pluginVersionMismatch: boolean;
  config: ExportConfig;

  loadSystems: () => Promise<void>;
  exportSystems: (systemNames: string[], outputPathOverride?: string) => Promise<void>;
  exportAll: (outputPathOverride?: string) => Promise<void>;
  setConfig: (updates: Partial<ExportConfig>) => void;
  clearError: () => void;
  clearExportResult: () => void;
}

// Parameter mode collects the same rows as SystemName (only SystemTypeName
// changes the collect shape), so a cache loaded in one is valid in the other.
const collectionKeyFor = (groupBy: GroupBy): GroupBy =>
  groupBy === 'SystemTypeName' ? 'SystemTypeName' : 'SystemName';

export function useExportState(profile?: RunExportFlowOptions['profile']): UseExportStateReturn {
  const { modelDataStale, refreshModelData } = useActiveDocumentRefresh();
  const storedReport = usePcfValidationStore((state) => state.report);
  const reportStale = usePcfValidationStore((state) => state.reportStale);
  const reportContextFingerprint = usePcfValidationStore(
    (state) => state.reportContextFingerprint
  );
  const markReportStale = usePcfValidationStore((state) => state.markReportStale);
  const report = storedReport && !reportStale && !modelDataStale && reportContextFingerprint ? storedReport : null;
  const systemGroupingValue = useConfigurationStore((state) => state.menuConfiguration.systemGroupingValue);
  const groupingParameterName = useConfigurationStore(
    (state) => state.menuConfiguration.groupingParameterName
  );
  const updateMenuConfiguration = useConfigurationStore((state) => state.updateMenuConfiguration);
  const [rawSystems, setRawSystems] = useState<PipingSystem[]>([]);
  const [loading, setLoading] = useState(false);
  const [systemsStale, setSystemsStaleState] = useState(false);
  // Ref mirror so in-flight callbacks (export) can re-check staleness after
  // an awaited refresh — the state closure would still hold the old value.
  const systemsStaleRef = useRef(false);
  const setSystemsStale = useCallback((stale: boolean) => {
    systemsStaleRef.current = stale;
    setSystemsStaleState(stale);
  }, []);
  const [error, setError] = useState<string | null>(null);
  const [exporting, setExporting] = useState(false);
  const [exportProgress, setExportProgress] = useState<OperationProgressModel | null>(null);
  const [exportResult, setExportResult] = useState<ExportResult | null>(null);
  const [exportStep, setExportStep] = useState<ExportFlowStep | null>(null);
  const [exportTraceId, setExportTraceId] = useState<string | null>(null);
  const [pluginVersionMismatch, setPluginVersionMismatch] = useState(false);
  const [sessionExportCounts, setSessionExportCounts] = useState<Record<string, number>>({});
  const exportBatchOperation = useBatchOperation();
  // Ref mirror: the stable loadSystems callback must see whether a model
  // change is still pending adoption when a collect succeeds.
  const modelDataStaleRef = useRef(modelDataStale);
  const rawSystemsRef = useRef<PipingSystem[]>([]);
  const rawSystemsGroupByRef = useRef<GroupBy>('SystemName');
  const loadIdRef = useRef(0);
  const mountedRef = useRef(true);
  // Ref that always holds the latest groupBy — lets loadSystems read
  // the current value without being in its dependency array.
  const groupByRef = useRef<GroupBy>('SystemName');
  // Same mirror for the grouping parameter: exportSystems resolves selected
  // group labels back to member systems without re-creating its callback.
  const groupingParameterNameRef = useRef('');
  // Same mirror for the linked-model scope: collection and export both read
  // the current value without re-creating their callbacks.
  const includeLinkedModelsRef = useRef(false);
  const [config, setConfigState] = useState<ExportConfig>({
    groupBy: toGroupBy(systemGroupingValue),
    outputPath: readPersistedOutputPath(),
    selectedOnly: false,
    includeValidation: true,
    includeLinkedModels: false,
  });

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      loadIdRef.current += 1;
    };
  }, []);

  const isCurrentLoad = useCallback((loadId: number) => (
    mountedRef.current && loadIdRef.current === loadId
  ), []);

  const updateSystemLoadProgressFromHost = useCallback((progress: RevitOperationProgressSnapshot) => {
    if (!mountedRef.current) {
      return;
    }

    setExportProgress((current) => mapRevitOperationSnapshotToProgressModel(progress, {
      title: current?.title ?? 'Loading piping systems',
      message: 'Revit is collecting piping systems',
      completeMessage: 'Piping systems loaded',
      errorMessage: 'Piping system load failed',
      metrics: current?.metrics,
      locked: !progress.resultReady && !progress.failed,
    }));
  }, []);

  const discoverGrpcPortsFromHost = useCallback(async (): Promise<GrpcPortDiscovery> => {
    // The removed COM bridge now supplies its port through the navigation URL;
    // report it unavailable so discovery uses getGrpcPort().
    return {
      ports: [],
      hostBridgeAvailable: false,
      error: null,
    };
  }, []);

  const buildGrpcPortCandidates = useCallback(async (): Promise<GrpcPortDiscovery> => {
    const discovered = await discoverGrpcPortsFromHost();
    if (discovered.hostBridgeAvailable) {
      if (discovered.ports.length === 0) {
        setGrpcPort(null);
      }

      return discovered;
    }

    const orderedCandidates: number[] = [];
    const currentPort = getGrpcPort();
    if (currentPort && Number.isFinite(currentPort) && currentPort > 0) {
      orderedCandidates.push(currentPort);
    }

    for (const port of discovered.ports) {
      if (!orderedCandidates.includes(port)) {
        orderedCandidates.push(port);
      }
    }

    return {
      ports: orderedCandidates,
      hostBridgeAvailable: false,
      error: discovered.error,
    };
  }, [discoverGrpcPortsFromHost]);

  const normalizeCollectedSystems = useCallback((systems: any[]): PipingSystem[] => {
    return systems.map((item) => ({
      ...item,
      // Normalize server keys to UI column labels
      'PIPING-SPEC': getSystemField(item, 'PIPING-SPEC', 'PIPING_SPEC', 'PiP_PPG_SPEC', 'PiX_PPG_Spec'),
      MATERIAL: getSystemField(item, 'MATERIAL', 'Material', 'PiX_03_Material_ENG'),
      'DESIGN-STD': getSystemField(item, 'DESIGN-STD', 'DESIGN_STD', 'DesignStd', 'PiX_02_Standard_ENG'),
      'PRESSURE-CLASS': getSystemField(item, 'PRESSURE-CLASS', 'PRESSURE_CLASS', 'PressureClass', 'PiX_PPG_END1_PressureRating'),
      'INSULATION-SPEC': getSystemField(item, 'INSULATION-SPEC', 'INSULATION_SPEC', 'InsulationSpec', 'PiX_GN_2_InsulationSpec'),
      sourceSystemName: getSystemField(item, 'sourceSystemName', 'SourceSystemName'),
      sourceSystemNames: getSystemField(item, 'sourceSystemNames', 'SourceSystemNames'),
      systemTypeName: getSystemField(item, 'systemTypeName', 'SystemTypeName'),
    }));
  }, []);

  const setLoadedSystems = useCallback((nextSystems: PipingSystem[], groupBy: GroupBy) => {
    rawSystemsRef.current = nextSystems;
    rawSystemsGroupByRef.current = groupBy;
    setRawSystems(nextSystems);
  }, []);

  const recordSessionExports = useCallback((exportedSystems: PipingSystem[]) => {
    setSessionExportCounts((previous) => {
      const next = { ...previous };
      for (const system of exportedSystems) {
        const key = getSessionExportCountKey(system.systemName);
        if (!key) {
          continue;
        }

        next[key] = (next[key] ?? 0) + 1;
      }
      return next;
    });
  }, []);

  const loadSystems = useCallback(async () => {
    if (!mountedRef.current) {
      return;
    }

    const currentLoadId = ++loadIdRef.current;
    const traceId = createExportTraceId();
    const loadStartedAtMs = getExportTraceNowMs();
    const currentGroupBy = groupByRef.current;
    const bySystemType = currentGroupBy === 'SystemTypeName';
    const includeLinkedModels = includeLinkedModelsRef.current;

    setLoading(true);
    setError(null);
    await emitExportTrace(traceId, '01-system-load-start', {
      groupBy: currentGroupBy,
      bySystemType,
      includeLinkedModels,
    });
    if (!isCurrentLoad(currentLoadId)) return;

    try {
      logExportDebug('[useExportState] loadSystems', { groupBy: currentGroupBy, bySystemType });

      const portDiscovery = await buildGrpcPortCandidates();
      if (!isCurrentLoad(currentLoadId)) return;

      const portsToTry = portDiscovery.ports;
      await emitExportTrace(traceId, '02-system-load-ports-resolved', {
        elapsedMs: getExportTraceElapsedMs(loadStartedAtMs),
        ports: portsToTry,
        portCount: portsToTry.length,
        hostBridgeAvailable: portDiscovery.hostBridgeAvailable,
        discoveryError: portDiscovery.error,
      });
      if (portsToTry.length === 0) {
        const loadError = portDiscovery.error || normalizeTransportError('gRPC transport is unavailable', 'collect');
        await emitExportTrace(traceId, '03-system-load-no-ports', {
          elapsedMs: getExportTraceElapsedMs(loadStartedAtMs),
          error: loadError,
        });
        if (!isCurrentLoad(currentLoadId)) return;
        setError(loadError);
        return;
      }

      const failures: string[] = [];
      for (const [attemptIndex, port] of portsToTry.entries()) {
        if (!isCurrentLoad(currentLoadId)) return;
        setGrpcPort(port);
        logExportDebug('[useExportState] CollectSystems attempt', { port, bySystemType });
        const attemptStartedAtMs = getExportTraceNowMs();
        await emitExportTrace(traceId, '10-system-load-collect-start', {
          elapsedMs: getExportTraceElapsedMs(loadStartedAtMs),
          attemptIndex,
          port,
          bySystemType,
          transportMode: 'operation-progress',
        });

        const result = await collectPipingSystemsGrpc(currentGroupBy, {
          includeLinkedModels,
          onProgress: (progress) => {
            if (isCurrentLoad(currentLoadId)) {
              updateSystemLoadProgressFromHost(progress);
            }
          },
        });
        if (!isCurrentLoad(currentLoadId)) return;
        if (result.success) {
          const normalizedSystems = normalizeCollectedSystems(result.systems as any[]);
          logExportDebug('[useExportState] CollectSystems success', {
            bySystemType, systems: normalizedSystems.length,
          });
          await emitExportTrace(traceId, '11-system-load-collect-succeeded', {
            elapsedMs: getExportTraceElapsedMs(loadStartedAtMs),
            attemptElapsedMs: getExportTraceElapsedMs(attemptStartedAtMs),
            attemptIndex,
            port,
            bySystemType,
            systems: normalizedSystems.length,
            rawSystems: result.systems?.length ?? 0,
          });
          if (!isCurrentLoad(currentLoadId)) return;
          setExportProgress(null);
          setLoadedSystems(normalizedSystems, currentGroupBy);
          setSystemsStale(false);
          // The collect just snapshotted the live model with caches bypassed,
          // so adopt the pending ModelChanged version — the stale badge clears
          // without a second fetch. Guarded: with nothing pending,
          // refreshModelData() would still publish a manual-refresh
          // invalidation that stales other pages' data for no reason.
          if (modelDataStaleRef.current) {
            refreshModelData();
          }
          return;
        }

        const normalizedCollectError = normalizeTransportError(result.error, 'collect');
        failures.push(`[${port}] ${normalizedCollectError}`);
        console.warn('[useExportState] CollectSystems failed on port', port, result.error);
        await emitExportTrace(traceId, '12-system-load-collect-failed', {
          elapsedMs: getExportTraceElapsedMs(loadStartedAtMs),
          attemptElapsedMs: getExportTraceElapsedMs(attemptStartedAtMs),
          attemptIndex,
          port,
          bySystemType,
          error: normalizedCollectError,
        });
      }

      const combinedError = failures[0] || normalizeTransportError('Failed to load piping systems', 'collect');
      await emitExportTrace(traceId, '80-system-load-finished-failure', {
        elapsedMs: getExportTraceElapsedMs(loadStartedAtMs),
        failures,
        error: combinedError,
      });
      if (!isCurrentLoad(currentLoadId)) return;
      setError(combinedError);
    } catch (err: unknown) {
      if (!isCurrentLoad(currentLoadId)) return;
      const loadError = isUnknownComMemberError(err)
        ? 'gRPC runtime bridge method is unavailable in this host session'
        : getExportErrorMessage(err, 'Failed to load piping systems');
      await emitExportTrace(traceId, '99-system-load-exception', {
        elapsedMs: getExportTraceElapsedMs(loadStartedAtMs),
        error: loadError,
      });
      if (!isCurrentLoad(currentLoadId)) return;
      setError(loadError);
    } finally {
      if (isCurrentLoad(currentLoadId)) {
        setLoading(false);
      }
    }
  // groupByRef.current is used inside instead of config.groupBy to avoid stale closures
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [buildGrpcPortCandidates, isCurrentLoad, normalizeCollectedSystems, refreshModelData, setLoadedSystems, updateSystemLoadProgressFromHost]);

  useEffect(() => {
    modelDataStaleRef.current = modelDataStale;
  }, [modelDataStale]);

  // ModelChanged no longer fans out cache invalidations; the pending-version
  // signal from the active-document provider owns the stale badge now.
  useEffect(() => {
    if (modelDataStale) {
      markReportStale();
      setSystemsStale(rawSystemsRef.current.length > 0);
    }
  }, [markReportStale, modelDataStale, setSystemsStale]);
  useEffect(() => {
    const unregister = registerRevitModelCacheInvalidator((event) => {
      if (!mountedRef.current) {
        return;
      }

      // ModelChanged is handled through modelDataStale above; the events that
      // still reach this invalidator are document lifecycle ones (or explicit
      // refresh adoptions) — only those drop the loaded snapshot.
      if (event.eventType === 'ModelChanged') {
        return;
      }

      loadIdRef.current += 1;
      setLoadedSystems([], groupByRef.current);
      setSystemsStale(false);
      setSessionExportCounts({});
      setLoading(false);
      setExportProgress(null);
    });

    return () => {
      loadIdRef.current += 1;
      unregister();
    };
  }, [setLoadedSystems, setSystemsStale]);

  const systems = useMemo((): PipingSystem[] => {
    if (!report) {
      return rawSystems.map((system) => ({
        ...(isE2eMockSystemsRequested() ? system : markPipingSystemValidationPending(system)),
        sessionExportCount: sessionExportCounts[getSessionExportCountKey(system.systemName)] ?? 0,
      }));
    }

    const issues = report.CheckResults.flatMap((check) =>
      check.Issues.filter((issue) => isActionableValidationIssue(check, issue))
    );

    return rawSystems.map((system) => {
      const systemElementIds = new Set((system.elementIds || []).map((id) => String(id)));
      const hasSystemElementScope = systemElementIds.size > 0;
      const systemNames = new Set<string>([
        normalizeSystemNameToken(system.systemName),
        normalizeSystemNameToken(String((system as any).sourceSystemName || '')),
        ...parseSystemNames(String((system as any).sourceSystemNames || '')).map(normalizeSystemNameToken),
        normalizeSystemNameToken(String((system as any).systemTypeName || '')),
      ].filter((name) => name.length > 0 && !isGlobalSystemScopeName(name)));
      const hasSystemValidationIdentity = hasSystemElementScope || systemNames.size > 0;
      const validationScopeElementIds = new Set<string>();
      let warningCount = 0;
      let errorCount = 0;

      for (const issue of issues) {
        const touchesByElementId = systemElementIds.size > 0
          && Array.isArray(issue.ElementIds)
          && issue.ElementIds.some((id) => systemElementIds.has(String(id)));

        const issueSystemNames = collectIssueSystemNames(issue);
        const touchesBySystemName = !touchesByElementId
          && issueSystemNames.size > 0
          && Array.from(issueSystemNames).some((name) => systemNames.has(name));

        const touchesSystem = touchesByElementId || touchesBySystemName;
        if (!touchesSystem) continue;

        addIssueElementIds(validationScopeElementIds, issue);

        if (isErrorSeverity(issue.Severity)) {
          errorCount += 1;
        } else if (isWarningSeverity(issue.Severity)) {
          warningCount += 1;
        }
      }

      const status = errorCount > 0
        ? 'Errors'
        : warningCount > 0
          ? 'Warnings'
          : !hasSystemValidationIdentity && issues.length > 0
            ? 'NotValidated'
            : 'Ready';
      return {
        ...system,
        status,
        warningCount,
        errorCount,
        sessionExportCount: sessionExportCounts[getSessionExportCountKey(system.systemName)] ?? 0,
        validationScopeElementIds: Array.from(validationScopeElementIds).sort((left, right) =>
          left.localeCompare(right, undefined, { numeric: true })
        ),
        validationScopeSystemNames: Array.from(systemNames),
      };
    });
  }, [rawSystems, report, sessionExportCounts]);

  // Display-only grouping: Parameter mode folds the collected systems into one
  // row per parameter value so the table reflects the configured grouping.
  // Export keeps reading the flat `systems` list (its name matching depends on
  // it), so this derivation must never feed back into export logic.
  const viewSystems = useMemo((): PipingSystem[] => {
    if (config.groupBy !== 'Parameter' || !groupingParameterName.trim()) {
      return systems;
    }
    return groupSystemsByParameterValue(systems, groupingParameterName);
  }, [systems, config.groupBy, groupingParameterName]);

  const exportSystems = useCallback(async (systemNames: string[], outputPathOverride?: string) => {
    if (exportBatchOperation.isRunning) {
      return;
    }

    setExporting(true);
    setExportResult(null);
    setExportStep('license');
    setExportTraceId(null);
    setPluginVersionMismatch(false);
    setExportProgress({
      title: 'Exporting PCF files',
      message: '① Checking license and PCF export quota…',
      detail: outputPathOverride || config.outputPath
        ? `Output folder: ${outputPathOverride || config.outputPath}`
        : 'Waiting for the export output folder',
      status: 'running',
      percent: null,
      processed: 0,
      total: systemNames.length > 0 ? systemNames.length : null,
      locked: true,
      metrics: [
        { label: 'Requested systems', value: String(systemNames.length) },
      ],
    });

    const stepLabels: Record<ExportFlowStep, string> = {
      license: '① Checking license and PCF export quota…',
      calculate: '② Calculating PCF files in the PipeEX plugin…',
      write: '③ Writing and verifying PCF files…',
      record: '④ Recording confirmed PCF files…',
    };

    try {
      if (systemNames.length === 0) {
        setExportResult({
          success: false,
          systemsExported: 0,
          elementsExported: 0,
          error: 'No piping systems selected for export.',
        });
        return;
      }

      // The model changed since the systems list was collected — refresh it
      // before filtering, otherwise requested names miss the server-side
      // snapshot groups ("No matching system group was found").
      if (systemsStaleRef.current) {
        await loadSystems();
      }
      if (systemsStaleRef.current) {
        setExportResult({
          success: false,
          systemsExported: 0,
          elementsExported: 0,
          error: 'Piping systems changed in Revit and the list could not be refreshed. Resolve the load error and retry the export.',
        });
        return;
      }

      const requestedSystemNames = new Set(systemNames.map((name) => name.toLowerCase()));
      const resolvedOutputPath = outputPathOverride || config.outputPath || '';
      const currentGroupBy = groupByRef.current;
      const cachedSystems = collectionKeyFor(rawSystemsGroupByRef.current) === collectionKeyFor(currentGroupBy)
        ? rawSystemsRef.current
        : [];
      const isListSubsetSelection = systemNames.length !== cachedSystems.length;

      if (!resolvedOutputPath) {
        setExportResult({
          success: false,
          systemsExported: 0,
          elementsExported: 0,
          error: 'Choose an output folder before exporting.',
        });
        return;
      }

      if (cachedSystems.length === 0) {
        setExportResult({
          success: false,
          systemsExported: 0,
          elementsExported: 0,
          error: 'No loaded piping system snapshot is available. Refresh systems before exporting.',
        });
        return;
      }

      // In Parameter mode the table's selectable rows are group labels, not
      // system names — expand them to their member systems before matching.
      // Flat system-name callers (export all) still match directly.
      const groupSelectedNames = currentGroupBy === 'Parameter'
        ? new Set(
            filterSystemsByGroupValues(
              cachedSystems,
              groupingParameterNameRef.current,
              requestedSystemNames
            ).map((system) => system.systemName.toLowerCase())
          )
        : null;
      const systemsForExport = isListSubsetSelection
        ? cachedSystems.filter((system) => {
            const key = system.systemName.toLowerCase();
            return requestedSystemNames.has(key) || groupSelectedNames?.has(key) === true;
          })
        : cachedSystems;

      if (systemsForExport.length === 0) {
        setExportResult({
          success: false,
          systemsExported: 0,
          elementsExported: 0,
          error: 'No matching piping systems were found for export.',
        });
        return;
      }

      const flowResult = await runExportFlow(systemsForExport, {
        groupBy: currentGroupBy,
        outputPath: resolvedOutputPath,
        includeLinkedModels: includeLinkedModelsRef.current,
        // Parameter mode: scope the plugin's grouping to the selected group
        // labels. Export-all deliberately sends no filter (every group).
        groupFilter: currentGroupBy === 'Parameter' && isListSubsetSelection
          ? systemNames
          : undefined,
        profile,
        onStep: (step) => {
          setExportStep(step);
          setExportProgress((current) => ({
            title: current?.title ?? 'Exporting PCF files',
            message: stepLabels[step],
            detail: `Output folder: ${resolvedOutputPath}`,
            status: 'running',
            percent: null,
            processed: current?.processed ?? null,
            total: current?.total ?? null,
            locked: true,
            metrics: current?.metrics ?? [
              { label: 'Requested systems', value: String(systemsForExport.length) },
            ],
          }));
        },
        onProgress: (progress) => {
          setExportStep('calculate');
          setExportProgress(mapLocalPcfProgressToModel(
            progress,
            resolvedOutputPath,
            systemsForExport.length,
          ));
        },
      });

      setExportTraceId(flowResult.traceId);
      setPluginVersionMismatch(Boolean(flowResult.versionMismatch));

      const quotaStore = useExportQuotaStore.getState();
      if (flowResult.quotaExhausted) {
        quotaStore.applyExhausted(flowResult.isDemo, flowResult.remainingExportElements);
      } else {
        quotaStore.applyRunQuota(flowResult.remainingExportElements, flowResult.isDemo);
      }

      if (flowResult.success) {
        const failedNames = new Set((flowResult.groupFailures ?? [])
          .map((failure) => failure.systemName.toLowerCase()));
        const exportedSystems = systemsForExport.filter((system) => !failedNames.has(system.systemName.toLowerCase()));
        const totalElements = exportedSystems.reduce((sum, system) => sum + system.elementCount, 0);
        recordSessionExports(exportedSystems);
        setExportResult({
          success: true,
          systemsExported: flowResult.filesWritten ?? exportedSystems.length,
          elementsExported: flowResult.elementsWritten ?? totalElements,
          traceId: flowResult.traceId,
          filesWritten: flowResult.filesWritten,
          skippedFiles: flowResult.skippedFiles,
          fileIssues: flowResult.fileIssues,
          successfulFiles: flowResult.successfulFiles,
          groupFailures: flowResult.groupFailures,
          mappedHeaderWarning: flowResult.mappedHeaderWarning,
          filePath: flowResult.outputPath ?? resolvedOutputPath,
        });
      } else {
        setExportResult({
          success: false,
          systemsExported: 0,
          error: flowResult.error || 'PCF export failed.',
          errorCode: flowResult.errorCode,
          quotaExhausted: flowResult.quotaExhausted,
          quotaInsufficient: flowResult.quotaInsufficient,
          isDemo: flowResult.isDemo,
          remainingExportElements: flowResult.remainingExportElements,
          traceId: flowResult.traceId,
          failedStep: flowResult.step,
          versionMismatch: flowResult.versionMismatch,
          skippedFiles: flowResult.skippedFiles,
          fileIssues: flowResult.fileIssues,
          filesWritten: flowResult.filesWritten,
          successfulFiles: flowResult.successfulFiles,
          elementsExported: flowResult.elementsWritten ?? 0,
          filePath: flowResult.outputPath ?? resolvedOutputPath,
          groupFailures: flowResult.groupFailures,
        });
      }
    } catch (err: any) {
      setExportResult({
        success: false,
        systemsExported: 0,
        elementsExported: 0,
        error: getExportErrorMessage(err, 'Export failed'),
      });
    } finally {
      setExporting(false);
      setExportStep(null);
      setExportProgress(null);
    }
  }, [
    profile,
    config.outputPath,
    exportBatchOperation,
    loadSystems,
    recordSessionExports,
  ]);

  const exportAll = useCallback(async (outputPathOverride?: string) => {
    const allNames = systems.map(s => s.systemName);
    await exportSystems(allNames, outputPathOverride);
  }, [systems, exportSystems]);

  const setConfig = useCallback((updates: Partial<ExportConfig>) => {
    if (updates.groupBy) {
      updateMenuConfiguration({
        systemGroupingValue: toSystemGroupingPreference(updates.groupBy),
      });
    }
    // Rows collected under the previous linked-model scope describe a
    // different set of elements than the one the export would now write, so
    // they are stale until the reload below replaces them.
    if (updates.includeLinkedModels !== undefined
      && updates.includeLinkedModels !== includeLinkedModelsRef.current) {
      setSystemsStale(rawSystemsRef.current.length > 0);
    }
    setConfigState(prev => ({ ...prev, ...updates }));
  }, [setSystemsStale, updateMenuConfiguration]);

  // Keep groupByRef in sync with config state
  groupByRef.current = config.groupBy;
  includeLinkedModelsRef.current = config.includeLinkedModels;
  groupingParameterNameRef.current = groupingParameterName;

  // Sync store → local config when systemGroupingValue changes externally
  useEffect(() => {
    const mapped = toGroupBy(systemGroupingValue);
    setConfigState((prev) => (prev.groupBy === mapped ? prev : { ...prev, groupBy: mapped }));
  }, [systemGroupingValue]);

  // Persist the destination folder so it survives page reloads (paired
  // with readPersistedOutputPath above).
  useEffect(() => {
    try {
      if (typeof window === 'undefined') {
        return;
      }
      if (config.outputPath) {
        window.localStorage.setItem(EXPORT_OUTPUT_PATH_STORAGE_KEY, config.outputPath);
      } else {
        window.localStorage.removeItem(EXPORT_OUTPUT_PATH_STORAGE_KEY);
      }
    } catch {
      // Persistence is a convenience - ignore storage failures.
    }
  }, [config.outputPath]);

  const clearError = useCallback(() => setError(null), []);
  const clearExportResult = useCallback(() => setExportResult(null), []);

  // Auto-load after the workspace shell has painted, then reload when the
  // collected scope changes (grouping or linked-model inclusion).
  useEffect(() => {
    logExportDebug('[useExportState] Loading systems', {
      groupBy: config.groupBy,
      includeLinkedModels: config.includeLinkedModels,
    });
    return scheduleAfterInitialPaint(() => {
      void loadSystems();
    });
  }, [config.groupBy, config.includeLinkedModels, loadSystems]);

  return {
    systems,
    viewSystems,
    loading,
    systemsStale,
    error,
    exporting,
    exportProgress,
    exportResult,
    exportStep,
    exportTraceId,
    pluginVersionMismatch,
    config,
    loadSystems,
    exportSystems,
    exportAll,
    setConfig,
    clearError,
    clearExportResult,
  };
}
